// GifContext.js
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import {
  getEventScope,
  isCurrentScope,
  validateEventName,
} from "./eventIdentity";

const GifContext = createContext();

// Event named by a replay target such as "/swingies_X_01-01-2026", or null
// when the target is not an in-app event path with a usable name.
const eventOfNavigateTo = (navigateTo) => {
  if (typeof navigateTo !== "string" || !navigateTo.startsWith("/")) {
    return null;
  }
  try {
    return validateEventName(decodeURIComponent(navigateTo.split("/")[1] || ""));
  } catch (error) {
    return null;
  }
};

export const GifProvider = ({ children }) => {
  const [globalGif, setGlobalGif] = useState(null);
  const timerRef = useRef(null);
  // Scope the visible GIF belongs to (null = intentionally unscoped GIF).
  const gifScopeRef = useRef(null);

  // Show a GIF and, when it ends, optionally redirect. The GIF and its redirect
  // are bound to the event scope captured at schedule time: if that scope is no
  // longer the active one (another event, or a later visit of the same event)
  // the GIF is removed promptly (see dropStaleGif) and the redirect never
  // fires - a GIF from event A can neither cover nor redirect event B.
  const presentGif = (gifUrl, navigateTo, duration, scope) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    gifScopeRef.current = scope || null;
    setGlobalGif(gifUrl);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      gifScopeRef.current = null;
      setGlobalGif(null);
      if (!navigateTo) return;
      const target = eventOfNavigateTo(navigateTo);
      if (
        target !== null &&
        scope &&
        isCurrentScope(scope) &&
        scope.name === target
      ) {
        window.location.replace(navigateTo);
      }
    }, duration);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Check for pending GIF on every render
  useEffect(() => {
    // Clears the displayed GIF (and its timer) only when it is OWNED by a scope
    // that is no longer current; it runs before a new pending GIF is shown, so
    // it can never remove a GIF the new scope presented itself.
    const dropStaleGif = () => {
      const owner = gifScopeRef.current;
      if (!owner || isCurrentScope(owner)) return;
      gifScopeRef.current = null;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      setGlobalGif(null);
    };

    const checkPendingGif = () => {
      dropStaleGif();
      const pendingGifData = localStorage.getItem("pendingGif");
      if (pendingGifData) {
        try {
          const { gifUrl, navigateTo, duration, timestamp } =
            JSON.parse(pendingGifData);

          // Check if the GIF is not too old (not more than a minute)
          if (!(Date.now() - timestamp < 60000)) {
            // If the GIF is too old, just clear it
            localStorage.removeItem("pendingGif");
            return;
          }

          let scope = null;
          if (navigateTo) {
            const target = eventOfNavigateTo(navigateTo);
            if (target === null) {
              // Invalid sentinel (e.g. "/undefined"): drop only this entry.
              localStorage.removeItem("pendingGif");
              return;
            }
            // Generations do not survive a document reload, so a replay is
            // bound to the scope the gate activates for the matching event.
            // Until the gate is ready, leave the entry for the next tick.
            scope = getEventScope();
            if (!scope) return;
            if (scope.name !== target) {
              // Belongs to another event than the one on screen.
              localStorage.removeItem("pendingGif");
              return;
            }
          }

          // Remove from local storage before displaying
          localStorage.removeItem("pendingGif");
          presentGif(gifUrl, navigateTo, duration, scope);
        } catch (error) {
          console.error("Error processing pending GIF:", error);
          localStorage.removeItem("pendingGif");
        }
      }
    };

    // Check immediately on load
    checkPendingGif();

    // Check every second for a new GIF
    const intervalId = setInterval(checkPendingGif, 1000);
    return () => clearInterval(intervalId);
  }, []);

  const showGif = (gifUrl, navigateTo = null, duration = 30000) => {
    presentGif(gifUrl, navigateTo, duration, getEventScope());
  };

  return (
    <GifContext.Provider value={{ globalGif, showGif }}>
      {children}
      {globalGif && (
        <div
          className="gif-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <img src={globalGif} alt="Status GIF" className="status-gif" />
        </div>
      )}
    </GifContext.Provider>
  );
};

export const useGif = () => useContext(GifContext);
